﻿@file:OptIn(ExperimentalMaterial3Api::class)

package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.outlined.CalendarToday
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.roundToInt
import kotlin.time.Clock
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.ui.media.rememberMediaPickerState
import pl.zar.tikthrow.ui.theme.*
import androidx.lifecycle.viewmodel.compose.viewModel
import pl.zar.tikthrow.shared.domain.SocialMediaAccount
import pl.zar.tikthrow.viewmodel.AccountsStore
import pl.zar.tikthrow.viewmodel.AccountsViewModel
import pl.zar.tikthrow.viewmodel.ContentType
import pl.zar.tikthrow.viewmodel.ScheduledPostsViewModel

private val platformColors = mapOf(
    Platform.TWITTER   to Color(0xFF1D9BF0),
    Platform.INSTAGRAM to Color(0xFFE1306C),
    Platform.FACEBOOK  to Color(0xFF1877F2),
    Platform.TIKTOK    to Color(0xFFFF0050),
    Platform.YOUTUBE   to Color(0xFFFF0000),
    Platform.DISCORD   to Color(0xFF5865F2),
)

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))
private const val MILLIS_PER_DAY = 86_400_000L

private enum class PublishMode { NOW, SCHEDULED }

@Composable
fun SchedulePostScreen(
    viewModel: ScheduledPostsViewModel,
    type: ContentType,
    onBack: () -> Unit,
    editPostId: String? = null,
    defaultPlatforms: Set<Platform> = emptySet(),
) {
    val existing = remember(editPostId) { editPostId?.let { viewModel.getById(it) } }
    var media by remember { mutableStateOf(existing?.media ?: emptyList()) }
    var title by remember { mutableStateOf(existing?.title ?: "") }
    var caption by remember { mutableStateOf(existing?.caption ?: "") }
    var selectedPlatforms by remember { mutableStateOf(existing?.platforms ?: defaultPlatforms) }
    val accountsViewModel: AccountsViewModel = viewModel { AccountsViewModel() }
    LaunchedEffect(Unit) {
        if (AccountsStore.accounts.value.none { it.platform == Platform.YOUTUBE }) {
            accountsViewModel.refreshYouTubeStatus()
        }
        if (AccountsStore.accounts.value.none { it.platform == Platform.TIKTOK }) {
            accountsViewModel.refreshTikTokStatus()
        }
        accountsViewModel.refreshMetaStatus()
    }
    val allAccounts by AccountsStore.accounts.collectAsState()
    val youtubeAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.YOUTUBE } }
    val tiktokAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.TIKTOK } }
    val facebookAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.FACEBOOK } }
    val instagramAccounts = remember(allAccounts) { allAccounts.filter { it.platform == Platform.INSTAGRAM } }
    var selectedYouTubeAccountId by remember { mutableStateOf(existing?.youtubeAccountId) }
    var selectedTikTokAccountId by remember { mutableStateOf(existing?.tiktokAccountId) }
    var selectedFacebookAccountId by remember { mutableStateOf(existing?.facebookAccountId) }
    var selectedInstagramAccountId by remember { mutableStateOf(existing?.instagramAccountId) }

    LaunchedEffect(type) {
        if (type == ContentType.POST && Platform.YOUTUBE in selectedPlatforms) {
            selectedPlatforms = selectedPlatforms - Platform.YOUTUBE
        }
    }

    val nowMillis = remember { Clock.System.now().toEpochMilliseconds() }
    val todayStartMillis = remember(nowMillis) { nowMillis - (nowMillis % MILLIS_PER_DAY) }
    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = existing?.scheduledDateMillis ?: nowMillis,
        selectableDates = object : SelectableDates {
            override fun isSelectableDate(utcTimeMillis: Long): Boolean {
                return utcTimeMillis >= todayStartMillis
            }
        }
    )
    val timePickerState = rememberTimePickerState(
        initialHour = existing?.scheduledHour ?: 12,
        initialMinute = existing?.scheduledMinute ?: 0,
        is24Hour = true
    )

    var showDatePicker by remember { mutableStateOf(false) }
    var showTimePicker by remember { mutableStateOf(false) }
    var scheduleError by remember { mutableStateOf<String?>(null) }
    // Immediate publishing sends the post to the real platforms right away, so it's gated behind
    // an explicit confirmation that lists where it's about to go.
    var showPublishConfirm by remember { mutableStateOf(false) }

    val picker = rememberMediaPickerState(contentType = type) { newMedia -> media = media + newMedia }
    val isEditing = existing != null
    // New posts default to publishing immediately; editing an existing scheduled post stays scheduled.
    var publishMode by remember { mutableStateOf(if (isEditing) PublishMode.SCHEDULED else PublishMode.NOW) }
    val selectedDateMillis = datePickerState.selectedDateMillis
    val isImmediate = publishMode == PublishMode.NOW
    val isFutureSchedule = selectedDateMillis?.let {
        viewModel.isFutureSchedule(it, timePickerState.hour, timePickerState.minute)
    } ?: false
    val validationError = viewModel.validationError(
        type = type,
        media = media,
        caption = caption,
        platforms = selectedPlatforms,
        scheduledDateMillis = if (isImmediate) Clock.System.now().toEpochMilliseconds() else selectedDateMillis,
        scheduledHour = if (isImmediate) 0 else timePickerState.hour,
        scheduledMinute = if (isImmediate) 0 else timePickerState.minute,
        allowImmediate = isImmediate,
    )
    val canSave = validationError == null && (isImmediate || isFutureSchedule)

    /**
     * Actually saves the post: updates an existing one, publishes immediately, or schedules it for
     * later. Immediate publishing is invoked from the confirmation dialog — the bottom bar only
     * opens that dialog — so every path that reaches here is a deliberate choice to go live.
     */
    fun performSave() {
        val effectiveYouTubeAccountId = selectedYouTubeAccountId
            ?: youtubeAccounts.firstOrNull()?.id
        val effectiveTikTokAccountId = selectedTikTokAccountId
            ?: tiktokAccounts.firstOrNull()?.id
        val effectiveFacebookAccountId = selectedFacebookAccountId
            ?: facebookAccounts.firstOrNull()?.id
        val effectiveInstagramAccountId = selectedInstagramAccountId
            ?: instagramAccounts.firstOrNull()?.id
        val youtubeId = if (Platform.YOUTUBE in selectedPlatforms) effectiveYouTubeAccountId else null
        val tiktokId = if (Platform.TIKTOK in selectedPlatforms) effectiveTikTokAccountId else null
        val facebookId = if (Platform.FACEBOOK in selectedPlatforms) effectiveFacebookAccountId else null
        val instagramId = if (Platform.INSTAGRAM in selectedPlatforms) effectiveInstagramAccountId else null

        val saved = when {
            isEditing && existing != null -> viewModel.update(
                id = existing.id,
                media = media,
                title = title,
                caption = caption,
                platforms = selectedPlatforms,
                scheduledDateMillis = datePickerState.selectedDateMillis ?: return,
                scheduledHour = timePickerState.hour,
                scheduledMinute = timePickerState.minute,
                youtubeAccountId = youtubeId,
                tiktokAccountId = tiktokId,
                facebookAccountId = facebookId,
                instagramAccountId = instagramId,
            )
            isImmediate -> viewModel.publishNow(
                type = type,
                media = media,
                title = title,
                caption = caption,
                platforms = selectedPlatforms,
                youtubeAccountId = youtubeId,
                tiktokAccountId = tiktokId,
                facebookAccountId = facebookId,
                instagramAccountId = instagramId,
            )
            else -> {
                val dateMillis = datePickerState.selectedDateMillis ?: return
                viewModel.save(
                    type = type,
                    media = media,
                    title = title,
                    caption = caption,
                    platforms = selectedPlatforms,
                    scheduledDateMillis = dateMillis,
                    scheduledHour = timePickerState.hour,
                    scheduledMinute = timePickerState.minute,
                    youtubeAccountId = youtubeId,
                    tiktokAccountId = tiktokId,
                    facebookAccountId = facebookId,
                    instagramAccountId = instagramId,
                )
            }
        }
        if (saved) {
            onBack()
        } else {
            scheduleError = validationError ?: viewModel.validationError(
                type = type,
                media = media,
                caption = caption,
                platforms = selectedPlatforms,
                scheduledDateMillis = if (isImmediate) Clock.System.now().toEpochMilliseconds() else datePickerState.selectedDateMillis,
                scheduledHour = if (isImmediate) 0 else timePickerState.hour,
                scheduledMinute = if (isImmediate) 0 else timePickerState.minute,
                allowImmediate = isImmediate,
            )
        }
    }

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Column(modifier = Modifier.fillMaxSize()) {
            ScheduleTopBar(onBack = onBack, type = type, isEditing = isEditing)

            Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
                Column(
                    modifier = Modifier
                        .widthIn(max = 480.dp)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    PublishModeToggle(
                        isImmediate = isImmediate,
                        onImmediate = {
                            publishMode = PublishMode.NOW
                            scheduleError = null
                        },
                        onScheduled = {
                            publishMode = PublishMode.SCHEDULED
                            scheduleError = null
                        },
                    )
                    if (isImmediate) {
                        Surface(
                            shape = RoundedCornerShape(6.dp),
                            color = Slate200,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text(
                                text = "Post zostanie opublikowany natychmiast po zapisie.",
                                modifier = Modifier.padding(horizontal = 14.dp, vertical = 10.dp),
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                                color = Slate600,
                            )
                        }
                    } else {
                        ScheduleSection(
                            datePickerState = datePickerState,
                            timePickerState = timePickerState,
                            error = scheduleError,
                            onShowDatePicker = { showDatePicker = true },
                            onShowTimePicker = { showTimePicker = true },
                        )
                    }
                    MediaSection(
                        type = type,
                        media = media,
                        maxUploadSizeBytes = ScheduledPostsViewModel.MAX_UPLOAD_SIZE_BYTES,
                        onAddMore = { picker.launch() }
                    )
                    TitleSection(title = title, onTitleChange = { title = it })
                    CaptionSectionS(
                        caption = caption,
                        onCaptionChange = { caption = it },
                        onGenerate = { caption = "✨ Tu pojawi się wygenerowany opis na podstawie Twoich mediów i wybranych platform." }
                    )
                    PlatformSectionS(
                        type = type,
                        selected = selectedPlatforms,
                        onToggle = { platform ->
                            if (!platform.isAvailableFor(type)) return@PlatformSectionS
                            selectedPlatforms = if (platform in selectedPlatforms)
                                selectedPlatforms - platform
                            else
                                selectedPlatforms + platform
                            scheduleError = null
                        },
                        youtubeAccounts = youtubeAccounts,
                        selectedYouTubeAccountId = selectedYouTubeAccountId,
                        onSelectYouTubeAccount = { selectedYouTubeAccountId = it },
                        tiktokAccounts = tiktokAccounts,
                        selectedTikTokAccountId = selectedTikTokAccountId,
                        onSelectTikTokAccount = { selectedTikTokAccountId = it },
                        facebookAccounts = facebookAccounts,
                        selectedFacebookAccountId = selectedFacebookAccountId,
                        onSelectFacebookAccount = { selectedFacebookAccountId = it },
                        instagramAccounts = instagramAccounts,
                        selectedInstagramAccountId = selectedInstagramAccountId,
                        onSelectInstagramAccount = { selectedInstagramAccountId = it },
                    )
                    Spacer(Modifier.height(8.dp))
                }
            }

            ScheduleBottomBar(
                isEditing = isEditing,
                type = type,
                isImmediate = isImmediate,
                enabled = canSave,
                onSave = {
                    // Immediate publishing is one tap away from going live on every selected
                    // platform, so it needs an explicit "yes" first. Scheduled/edited posts
                    // keep the old one-step save.
                    if (isImmediate) {
                        showPublishConfirm = true
                    } else {
                        performSave()
                    }
                })
        }
    }

    if (showDatePicker) {
        DatePickerDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = {
                TextButton(onClick = { showDatePicker = false }) {
                    Text("OK", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDatePicker = false }) {
                    Text("Anuluj", color = Slate500)
                }
            },
            colors = DatePickerDefaults.colors(containerColor = Slate100),
            shape = RoundedCornerShape(8.dp)
        ) {
            DatePicker(
                state = datePickerState,
                colors = DatePickerDefaults.colors(
                    containerColor = Slate100,
                    selectedDayContainerColor = GradientStart,
                    selectedDayContentColor = Color.White,
                    todayDateBorderColor = GradientStart,
                    todayContentColor = GradientStart,
                    selectedYearContainerColor = GradientStart,
                    selectedYearContentColor = Color.White,
                )
            )
        }
    }

    if (showTimePicker) {
        AlertDialog(
            onDismissRequest = { showTimePicker = false },
            title = { Text("Godzina publikacji", fontWeight = FontWeight.ExtraBold, color = Slate900) },
            text = {
                Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    TimeInput(
                        state = timePickerState,
                        colors = TimePickerDefaults.colors(
                            timeSelectorSelectedContainerColor = GradientStart.copy(alpha = 0.12f),
                            timeSelectorSelectedContentColor = GradientStart,
                            timeSelectorUnselectedContainerColor = Slate100,
                            timeSelectorUnselectedContentColor = Slate800,
                            periodSelectorSelectedContainerColor = GradientStart,
                            periodSelectorSelectedContentColor = Color.White,
                            periodSelectorUnselectedContainerColor = Slate100,
                            periodSelectorUnselectedContentColor = Slate600,
                        )
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    scheduleError = null
                    showTimePicker = false
                }) {
                    Text("OK", color = GradientStart, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                }
            },
            dismissButton = {
                TextButton(onClick = { showTimePicker = false }) {
                    Text("Anuluj", color = Slate500)
                }
            },
            containerColor = Slate100,
            shape = RoundedCornerShape(8.dp)
        )
    }

    if (showPublishConfirm) {
        AlertDialog(
            onDismissRequest = { showPublishConfirm = false },
            title = {
                Text(
                    text = "Opublikować teraz?",
                    fontWeight = FontWeight.ExtraBold,
                    color = Slate900,
                    fontSize = 17.sp
                )
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(
                        text = "Post trafi od razu na wybrane platformy — nie będzie można tego cofnąć.",
                        fontSize = 13.sp,
                        color = Slate600
                    )
                    Text(
                        text = "PUBLIKUJ NA",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.ExtraBold,
                        letterSpacing = 1.5.sp,
                        color = Slate400
                    )
                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Platform.entries
                            .filter { it in selectedPlatforms }
                            .forEach { platform ->
                                val color = platformColors[platform] ?: Slate600
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Box(
                                        modifier = Modifier
                                            .size(10.dp)
                                            .clip(RoundedCornerShape(2.dp))
                                            .background(color)
                                    )
                                    Text(
                                        text = platform.displayName,
                                        fontSize = 14.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = Slate900
                                    )
                                }
                            }
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = {
                    showPublishConfirm = false
                    performSave()
                }) {
                    Text("Opublikuj", color = GradientStart, fontWeight = FontWeight.ExtraBold, fontSize = 15.sp)
                }
            },
            dismissButton = {
                TextButton(onClick = { showPublishConfirm = false }) {
                    Text("Anuluj", color = Slate500)
                }
            },
            containerColor = Slate100,
            shape = RoundedCornerShape(8.dp)
        )
    }
}

@Composable
private fun PublishModeToggle(
    isImmediate: Boolean,
    onImmediate: () -> Unit,
    onScheduled: () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(
            text = "KIEDY OPUBLIKOWAĆ",
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            letterSpacing = 1.5.sp,
            color = Slate400
        )
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Surface(
                onClick = onImmediate,
                shape = RoundedCornerShape(6.dp),
                color = if (isImmediate) Slate200 else Slate100,
                border = BorderStroke(1.5.dp, if (isImmediate) Slate600 else Slate200),
                modifier = Modifier.weight(1f)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    RadioButton(
                        selected = isImmediate,
                        onClick = onImmediate,
                        colors = RadioButtonDefaults.colors(
                            selectedColor = GradientStart,
                            unselectedColor = Slate300,
                        ),
                    )
                    Text(
                        text = "Od razu",
                        fontSize = 14.sp,
                        fontWeight = if (isImmediate) FontWeight.Bold else FontWeight.Medium,
                        color = if (isImmediate) Slate900 else Slate600,
                    )
                }
            }
            Surface(
                onClick = onScheduled,
                shape = RoundedCornerShape(6.dp),
                color = if (!isImmediate) Slate200 else Slate100,
                border = BorderStroke(1.5.dp, if (!isImmediate) Slate600 else Slate200),
                modifier = Modifier.weight(1f)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    RadioButton(
                        selected = !isImmediate,
                        onClick = onScheduled,
                        colors = RadioButtonDefaults.colors(
                            selectedColor = GradientStart,
                            unselectedColor = Slate300,
                        ),
                    )
                    Text(
                        text = "Ustal datę",
                        fontSize = 14.sp,
                        fontWeight = if (!isImmediate) FontWeight.Bold else FontWeight.Medium,
                        color = if (!isImmediate) Slate900 else Slate600,
                    )
                }
            }
        }
    }
}

@Composable
private fun ScheduleTopBar(onBack: () -> Unit, type: ContentType, isEditing: Boolean) {
    Surface(color = Slate100, shadowElevation = 2.dp) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(horizontal = 8.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            IconButton(onClick = onBack) {
                Icon(Icons.Default.ArrowBack, contentDescription = "Wróć", tint = Slate800)
            }
            Column {
                Text(
                    text = if (isEditing) "Edytuj post" else "${type.emoji} ${type.label}",
                    style = TextStyle(
                        brush = gradientBrush,
                        fontSize = 20.sp,
                        fontWeight = FontWeight.ExtraBold,
                    )
                )
                if (!isEditing) {
                    Text("Zaplanuj publikację", fontSize = 12.sp, color = Slate500)
                }
            }
        }
    }
}

@Composable
private fun ScheduleSection(
    datePickerState: DatePickerState,
    timePickerState: TimePickerState,
    error: String?,
    onShowDatePicker: () -> Unit,
    onShowTimePicker: () -> Unit,
) {
    val dateText = datePickerState.selectedDateMillis
        ?.let { formatDateMillis(it) }
        ?: "Wybierz datę"
    val timeText = "${timePickerState.hour.toString().padStart(2, '0')}:${timePickerState.minute.toString().padStart(2, '0')}"
    val hasDate = datePickerState.selectedDateMillis != null

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(
            text = "ZAPLANUJ NA",
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            letterSpacing = 1.5.sp,
            color = Slate400
        )
        Row(
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Surface(
                onClick = onShowDatePicker,
                shape = RoundedCornerShape(6.dp),
                color = if (hasDate) Slate200 else Slate100,
                border = BorderStroke(
                    1.5.dp,
                    if (hasDate) Slate600 else Slate200
                ),
                modifier = Modifier.weight(1f)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(Icons.Outlined.CalendarToday, contentDescription = null, modifier = Modifier.size(18.dp), tint = if (hasDate) Slate900 else Slate400)
                    Text(
                        text = dateText,
                        fontSize = 14.sp,
                        fontWeight = if (hasDate) FontWeight.SemiBold else FontWeight.Normal,
                        color = if (hasDate) Slate900 else Slate500
                    )
                }
            }
            Surface(
                onClick = onShowTimePicker,
                shape = RoundedCornerShape(6.dp),
                color = Slate200,
                border = BorderStroke(1.5.dp, Slate600),
                modifier = Modifier.width(110.dp)
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 14.dp, vertical = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(Icons.Outlined.Schedule, contentDescription = null, modifier = Modifier.size(18.dp), tint = Slate900)
                    Text(
                        text = timeText,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Slate900
                    )
                }
            }
        }
        error?.let {
            Text(
                text = it,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.error
            )
        }
    }
}

@Composable
private fun MediaSection(
    type: ContentType,
    media: List<MediaItem>,
    maxUploadSizeBytes: Long,
    onAddMore: () -> Unit
) {
    val emptyText = if (type == ContentType.POST) "Dodaj zdjecia" else "Dodaj wideo"
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(
            text = "MEDIA",
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            letterSpacing = 1.5.sp,
            color = Slate400
        )
        if (media.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(160.dp)
                    .clip(RoundedCornerShape(6.dp))
                    .background(Slate100)
                    .border(
                        width = 2.dp,
                        color = Slate300,
                        shape = RoundedCornerShape(6.dp)
                    )
                    .clickable { onAddMore() },
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(gradientBrush),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.Add, contentDescription = null, modifier = Modifier.size(22.dp), tint = Color.White)
                    }
                    Text(emptyText, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, color = Slate500)
                }
            }
        } else {
            LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                items(media) { item ->
                    val oversized = (item.fileSizeBytes ?: 0L) > maxUploadSizeBytes
                    val aspectRatio = if (item.isVideo) 9f / 16f else 1f
                    Box(
                        modifier = Modifier
                            .height(90.dp)
                            .aspectRatio(aspectRatio)
                            .clip(RoundedCornerShape(4.dp))
                            .background(Slate100)
                    ) {
                        ImageFromUri(uri = item.uri, isVideo = item.isVideo, modifier = Modifier.matchParentSize())
                        if (oversized) {
                            Box(
                                modifier = Modifier
                                    .matchParentSize()
                                    .background(Color(0xCC000000)),
                                contentAlignment = Alignment.Center
                            ) {
                                Column(
                                    horizontalAlignment = Alignment.CenterHorizontally,
                                    verticalArrangement = Arrangement.Center
                                ) {
                                    Text(
                                        text = "Za duży",
                                        fontSize = 10.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color(0xFFFF4444)
                                    )
                                    Text(
                                        text = formatFileSize(item.fileSizeBytes ?: 0L),
                                        fontSize = 9.sp,
                                        color = Color.White
                                    )
                                }
                            }
                        } else if (item.isVideo && item.fileSizeBytes != null && item.fileSizeBytes > 0L) {
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .fillMaxWidth()
                                    .background(Color(0x99000000))
                                    .padding(vertical = 2.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = formatFileSize(item.fileSizeBytes),
                                    fontSize = 9.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = Color.White
                                )
                            }
                        }
                    }
                }
                item {
                    Box(
                        modifier = Modifier
                            .size(90.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(Slate100)
                            .border(
                                1.5.dp,
                                Slate300,
                                RoundedCornerShape(4.dp)
                            )
                            .clickable { onAddMore() },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.Add, contentDescription = "Dodaj więcej", tint = Slate600)
                    }
                }
            }
            val totalSize = media.sumOf { it.fileSizeBytes ?: 0L }
            val sizeText = if (totalSize > 0L) " · ${formatFileSize(totalSize)}" else ""
            Text("${media.size} plik${if (media.size == 1) "" else "i/ów"} wybranych$sizeText", fontSize = 12.sp, color = Slate500)
        }
    }
}

@Composable
private fun TitleSection(title: String, onTitleChange: (String) -> Unit) {
    OutlinedTextField(
        value = title,
        onValueChange = { if (it.length <= 100) onTitleChange(it) },
        label = { Text("Tytuł") },
        placeholder = { Text("Tytuł (np. dla YouTube)", color = Slate400) },
        singleLine = true,
        modifier = Modifier.fillMaxWidth(),
        trailingIcon = {
            if (title.isNotEmpty()) {
                Text(
                    text = "${title.length}/100",
                    fontSize = 11.sp,
                    color = if (title.length >= 90) MaterialTheme.colorScheme.error else Slate400,
                    modifier = Modifier.padding(end = 8.dp)
                )
            }
        },
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = Slate600,
            unfocusedBorderColor = Slate200,
            focusedTextColor = Slate900,
            unfocusedTextColor = Slate900,
            cursorColor = Slate800,
            focusedContainerColor = Slate50,
            unfocusedContainerColor = Slate50,
        ),
        shape = RoundedCornerShape(6.dp)
    )
}

@Composable
private fun CaptionSectionS(caption: String, onCaptionChange: (String) -> Unit, onGenerate: () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.End
        ) {
            Surface(onClick = onGenerate, shape = RoundedCornerShape(8.dp), color = Color.Transparent) {
                Box(
                    modifier = Modifier
                        .background(Slate200, RoundedCornerShape(8.dp))
                        .padding(horizontal = 12.dp, vertical = 6.dp)
                ) {
                    Text("✨ Generuj opis", fontSize = 12.sp, fontWeight = FontWeight.Bold, color = Slate800)
                }
            }
        }
        OutlinedTextField(
            value = caption,
            onValueChange = onCaptionChange,
            label = { Text("Opis") },
            placeholder = { Text("Opis publikacji (YouTube / TikTok)", color = Slate400) },
            modifier = Modifier.fillMaxWidth().heightIn(min = 100.dp),
            maxLines = 6,
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = Slate600,
                unfocusedBorderColor = Slate200,
                focusedTextColor = Slate900,
                unfocusedTextColor = Slate900,
                cursorColor = Slate800,
                focusedContainerColor = Slate50,
                unfocusedContainerColor = Slate50,
            ),
            shape = RoundedCornerShape(6.dp)
        )
    }
}

@Composable
private fun PlatformSectionS(
    type: ContentType,
    selected: Set<Platform>,
    onToggle: (Platform) -> Unit,
    youtubeAccounts: List<SocialMediaAccount> = emptyList(),
    selectedYouTubeAccountId: String? = null,
    onSelectYouTubeAccount: (String) -> Unit = {},
    tiktokAccounts: List<SocialMediaAccount> = emptyList(),
    selectedTikTokAccountId: String? = null,
    onSelectTikTokAccount: (String) -> Unit = {},
    facebookAccounts: List<SocialMediaAccount> = emptyList(),
    selectedFacebookAccountId: String? = null,
    onSelectFacebookAccount: (String) -> Unit = {},
    instagramAccounts: List<SocialMediaAccount> = emptyList(),
    selectedInstagramAccountId: String? = null,
    onSelectInstagramAccount: (String) -> Unit = {},
) {
    val youtubeRed = Color(0xFFFF0000)
    val tiktokPink = Color(0xFFFF0050)
    val facebookBlue = Color(0xFF1877F2)
    val instagramGradient = Color(0xFFE1306C)
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("PUBLIKUJ NA", fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, letterSpacing = 1.5.sp, color = Slate400)
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Platform.entries.forEach { platform ->
                val isAvailable = platform.isAvailableFor(type)
                val isSelected = platform in selected
                val color = platformColors[platform] ?: Slate600
                Surface(
                    onClick = { if (isAvailable) onToggle(platform) },
                    shape = RoundedCornerShape(6.dp),
                    color = if (isSelected) Slate200 else Slate100,
                    shadowElevation = if (isSelected) 0.dp else 1.dp,
                    border = if (isSelected) BorderStroke(1.5.dp, Slate600) else BorderStroke(1.dp, Slate200)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(10.dp)
                                    .clip(RoundedCornerShape(2.dp))
                                    .background(color)
                            )
                            Text(
                                text = if (isAvailable) platform.displayName else "${platform.displayName} - Wideo/Shorts",
                                fontSize = 15.sp,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                color = when {
                                    !isAvailable -> Slate400
                                    isSelected -> Slate900
                                    else -> Slate800
                                }
                            )
                        }
                        Checkbox(
                            checked = isSelected,
                            onCheckedChange = { if (isAvailable) onToggle(platform) },
                            enabled = isAvailable,
                            colors = CheckboxDefaults.colors(
                                checkedColor = Slate800,
                                uncheckedColor = Slate300,
                                checkmarkColor = Slate50
                            )
                        )
                    }
                }
                if (platform == Platform.YOUTUBE && isSelected && youtubeAccounts.isNotEmpty()) {
                    AccountPickerRow(
                        accounts = youtubeAccounts,
                        selectedId = selectedYouTubeAccountId,
                        accentColor = youtubeRed,
                        onSelect = onSelectYouTubeAccount,
                    )
                }
                if (platform == Platform.TIKTOK && isSelected && tiktokAccounts.isNotEmpty()) {
                    AccountPickerRow(
                        accounts = tiktokAccounts,
                        selectedId = selectedTikTokAccountId,
                        accentColor = tiktokPink,
                        onSelect = onSelectTikTokAccount,
                    )
                }
                if (platform == Platform.FACEBOOK && isSelected && facebookAccounts.isNotEmpty()) {
                    AccountPickerRow(
                        accounts = facebookAccounts,
                        selectedId = selectedFacebookAccountId,
                        accentColor = facebookBlue,
                        onSelect = onSelectFacebookAccount,
                    )
                }
                if (platform == Platform.INSTAGRAM && isSelected && instagramAccounts.isNotEmpty()) {
                    AccountPickerRow(
                        accounts = instagramAccounts,
                        selectedId = selectedInstagramAccountId,
                        accentColor = instagramGradient,
                        onSelect = onSelectInstagramAccount,
                    )
                }
            }
        }
    }
}

@Composable
private fun AccountPickerRow(
    accounts: List<SocialMediaAccount>,
    selectedId: String?,
    accentColor: Color,
    onSelect: (String) -> Unit,
) {
    Column(
        modifier = Modifier.padding(start = 12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        accounts.forEach { account ->
            val isAccountSelected = account.id == selectedId ||
                (selectedId == null && account == accounts.first())
            Surface(
                onClick = { onSelect(account.id) },
                shape = RoundedCornerShape(4.dp),
                color = if (isAccountSelected) accentColor.copy(alpha = 0.10f) else Slate100,
                border = if (isAccountSelected) BorderStroke(1.5.dp, accentColor.copy(0.35f)) else BorderStroke(1.dp, Slate200),
                shadowElevation = if (isAccountSelected) 0.dp else 1.dp,
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 14.dp, vertical = 10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = account.displayName ?: account.accountHandle,
                            fontSize = 14.sp,
                            fontWeight = if (isAccountSelected) FontWeight.SemiBold else FontWeight.Normal,
                            color = if (isAccountSelected) accentColor else Slate600,
                        )
                        if (account.displayName != null) {
                            Text(
                                text = account.accountHandle,
                                fontSize = 11.sp,
                                color = Slate500,
                            )
                        }
                    }
                    RadioButton(
                        selected = isAccountSelected,
                        onClick = { onSelect(account.id) },
                        colors = RadioButtonDefaults.colors(
                            selectedColor = accentColor,
                            unselectedColor = Slate300,
                        ),
                    )
                }
            }
        }
    }
}

private fun Platform.isAvailableFor(type: ContentType): Boolean {
    return this != Platform.YOUTUBE || type != ContentType.POST
}

@Composable
private fun ScheduleBottomBar(
    isEditing: Boolean,
    type: ContentType,
    isImmediate: Boolean,
    enabled: Boolean,
    onSave: () -> Unit,
) {
    Surface(color = Slate100, shadowElevation = 8.dp) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding()
                .padding(horizontal = 16.dp, vertical = 14.dp)
                .height(52.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(
                    if (enabled) gradientBrush
                    else Brush.linearGradient(listOf(Slate200, Slate200))
                )
                .clickable(enabled = enabled, onClick = onSave),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = when {
                    isEditing -> "Zapisz zmiany"
                    isImmediate -> "Opublikuj teraz ${type.emoji} ${type.label}"
                    else -> "Zaplanuj ${type.emoji} ${type.label}"
                },
                color = if (enabled) Color.White else Slate400,
                fontSize = 15.sp,
                fontWeight = FontWeight.ExtraBold,
                letterSpacing = (-0.3).sp
            )
        }
    }
}
private fun formatFileSize(bytes: Long): String {
    return when {
        bytes >= 1024L * 1024L * 1024L -> {
            val gb = bytes / (1024.0 * 1024.0 * 1024.0)
            "${formatOneDecimal(gb)} GB"
        }
        bytes >= 1024L * 1024L -> {
            val mb = bytes / (1024.0 * 1024.0)
            if (mb >= 100) "${mb.roundToInt()} MB" else "${formatOneDecimal(mb)} MB"
        }
        bytes >= 1024L -> {
            val kb = bytes / 1024.0
            "${kb.roundToInt()} KB"
        }
        else -> "$bytes B"
    }
}

private fun formatOneDecimal(value: Double): String {
    val tenths = (value * 10).roundToInt()
    return "${tenths / 10}.${tenths % 10}"
}
